import { ArrowDownTrayIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import { payments as stubPayments } from '../../utils/stubData';
import Sidebar from '../common/Sidebar';

export default function PaymentHistory() {
  const [filters, setFilters] = useState({
    year: 'all',
    status: 'all',
  });

  const handleDownloadReceipt = payment => {
    // In a real app, this would download the PDF receipt
    alert(
      `Downloading receipt ${payment.receiptNumber} for payment of £${payment.amount.toFixed(2)}`
    );
  };

  const handleFilterChange = e => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Filter to show only completed payments for the current member (demo)
  const memberPayments = stubPayments
    .filter(p => p.membershipNumber === 'HDL001') // In real app, filter by current member
    .filter(payment => {
      const yearMatch = filters.year === 'all' || payment.year.toString() === filters.year;
      const statusMatch = filters.status === 'all' || payment.status === filters.status;
      return yearMatch && statusMatch;
    })
    .sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate));

  const totalPaid = memberPayments
    .filter(p => p.status === 'completed')
    .reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="min-h-screen bg-hdlcc-black">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-64">
          <div className="p-8">
            <div className="bg-gradient-to-br from-hdlcc-darkGray via-hdlcc-darkGray to-hdlcc-mediumGray text-white p-8 rounded-2xl shadow-elevation-2 border border-hdlcc-mediumGray/30">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-hdlcc-textPrimary tracking-tight">
                    Payment History
                  </h2>
                  <p className="text-hdlcc-textSecondary mt-1">View all your membership payments</p>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-gradient-to-r from-hdlcc-primary/15 to-hdlcc-primary/5 border border-hdlcc-primary/30 p-5 rounded-xl mb-8 shadow-elevation-1">
                <div className="flex items-start">
                  <DocumentTextIcon className="w-6 h-6 text-hdlcc-primary mr-3 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-hdlcc-textSecondary leading-relaxed">
                    <strong className="text-hdlcc-primary font-semibold">Payment Receipts:</strong>{' '}
                    Download receipts for completed payments by clicking the download button. All
                    receipts include transaction details and membership information for your
                    records.
                  </p>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
                <div className="bg-gradient-to-br from-hdlcc-mediumGray to-hdlcc-gray border border-hdlcc-lightGray/20 p-6 rounded-xl shadow-elevation-1">
                  <h4 className="text-hdlcc-textTertiary text-sm font-semibold">Total Paid</h4>
                  <p className="text-4xl font-bold mt-2 text-hdlcc-textPrimary">
                    £{totalPaid.toFixed(2)}
                  </p>
                  <p className="text-sm text-hdlcc-textTertiary mt-1">All time</p>
                </div>

                <div className="bg-gradient-to-br from-hdlcc-mediumGray to-hdlcc-gray border border-hdlcc-lightGray/20 p-6 rounded-xl shadow-elevation-1">
                  <h4 className="text-hdlcc-textTertiary text-sm font-semibold">
                    Payments This Year
                  </h4>
                  <p className="text-4xl font-bold mt-2 text-hdlcc-textPrimary">
                    {memberPayments.filter(p => p.year === new Date().getFullYear()).length}
                  </p>
                  <p className="text-sm text-hdlcc-textTertiary mt-1">{new Date().getFullYear()}</p>
                </div>

                <div className="bg-gradient-to-br from-hdlcc-mediumGray to-hdlcc-gray border border-hdlcc-lightGray/20 p-6 rounded-xl shadow-elevation-1">
                  <h4 className="text-hdlcc-textTertiary text-sm font-semibold">Last Payment</h4>
                  <p className="text-2xl font-bold mt-2 text-hdlcc-textPrimary">
                    {memberPayments.length > 0
                      ? new Date(memberPayments[0].paymentDate).toLocaleDateString()
                      : 'N/A'}
                  </p>
                  <p className="text-sm text-hdlcc-textTertiary mt-1">
                    {memberPayments.length > 0 ? `£${memberPayments[0].amount.toFixed(2)}` : ''}
                  </p>
                </div>
              </div>

              {/* Filters */}
              <div className="bg-hdlcc-mediumGray border border-hdlcc-lightGray/20 p-6 rounded-xl shadow-elevation-1 mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label
                      htmlFor="payment-history-year"
                      className="block text-sm text-hdlcc-textSecondary font-semibold mb-2"
                    >
                      Year
                    </label>
                    <select
                      id="payment-history-year"
                      name="year"
                      value={filters.year}
                      onChange={handleFilterChange}
                      className="w-full p-3 border rounded-lg bg-hdlcc-gray text-hdlcc-textPrimary border-hdlcc-lightGray/30 focus:border-hdlcc-primary focus:outline-none focus:ring-2 focus:ring-hdlcc-primary/20 transition-all"
                    >
                      <option value="all">All Years</option>
                      <option value="2025">2025</option>
                      <option value="2024">2024</option>
                      <option value="2023">2023</option>
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="payment-history-status"
                      className="block text-sm text-hdlcc-textSecondary font-semibold mb-2"
                    >
                      Status
                    </label>
                    <select
                      id="payment-history-status"
                      name="status"
                      value={filters.status}
                      onChange={handleFilterChange}
                      className="w-full p-3 border rounded-lg bg-hdlcc-gray text-hdlcc-textPrimary border-hdlcc-lightGray/30 focus:border-hdlcc-primary focus:outline-none focus:ring-2 focus:ring-hdlcc-primary/20 transition-all"
                    >
                      <option value="all">All Statuses</option>
                      <option value="completed">Completed</option>
                      <option value="pending">Pending</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Payments Table */}
              <div className="bg-hdlcc-mediumGray border border-hdlcc-lightGray/20 rounded-xl shadow-elevation-1 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-hdlcc-lightGray/20 bg-hdlcc-gray/40">
                        <th className="px-6 py-4 text-left text-sm font-semibold text-hdlcc-textSecondary">
                          Date
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-hdlcc-textSecondary">
                          Receipt No.
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-hdlcc-textSecondary">
                          Description
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-hdlcc-textSecondary">
                          Amount
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-hdlcc-textSecondary">
                          Method
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-hdlcc-textSecondary">
                          Status
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-hdlcc-textSecondary">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {memberPayments.map(payment => (
                        <tr
                          key={payment.id}
                          className="border-b border-hdlcc-lightGray/10 hover:bg-hdlcc-gray/30 transition-colors"
                        >
                          <td className="px-6 py-4 text-sm text-hdlcc-textPrimary">
                            {new Date(payment.paymentDate).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-sm font-mono text-hdlcc-textSecondary">
                            {payment.receiptNumber || 'Pending'}
                          </td>
                          <td className="px-6 py-4 text-sm text-hdlcc-textPrimary">
                            Membership Fee - {payment.year}
                            {payment.isManual && (
                              <span className="ml-2 text-xs text-hdlcc-warning font-medium">
                                (Manual)
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm font-bold text-hdlcc-textPrimary">
                            £{payment.amount.toFixed(2)}
                          </td>
                          <td className="px-6 py-4 text-sm capitalize text-hdlcc-textSecondary">
                            {payment.paymentMethod.replace('_', ' ')}
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                                payment.status === 'completed'
                                  ? 'bg-hdlcc-success/20 text-hdlcc-success border border-hdlcc-success/30'
                                  : payment.status === 'pending'
                                    ? 'bg-hdlcc-warning/20 text-hdlcc-warning border border-hdlcc-warning/30'
                                    : 'bg-hdlcc-error/20 text-hdlcc-error border border-hdlcc-error/30'
                              }`}
                            >
                              {payment.status}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {payment.status === 'completed' && payment.receiptNumber && (
                              <button
                                onClick={() => handleDownloadReceipt(payment)}
                                className="flex items-center gap-2 text-hdlcc-primary hover:text-hdlcc-primaryLight text-sm font-medium transition-colors group"
                              >
                                <ArrowDownTrayIcon className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
                                Download
                              </button>
                            )}
                            {payment.status === 'pending' && (
                              <span className="text-hdlcc-textTertiary text-sm">Pending</span>
                            )}
                            {payment.status === 'failed' && (
                              <span className="text-hdlcc-error text-sm font-medium">Failed</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {memberPayments.length === 0 && (
                  <div className="text-center py-16 text-hdlcc-textSecondary">
                    <p className="text-lg">No payments found.</p>
                  </div>
                )}
              </div>

              {/* Payment Summary by Year */}
              {memberPayments.length > 0 && (
                <div className="mt-8 bg-hdlcc-mediumGray border border-hdlcc-lightGray/20 p-6 rounded-xl shadow-elevation-1">
                  <h3 className="text-xl font-bold mb-6 text-hdlcc-textPrimary">Annual Summary</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {[2025, 2024, 2023].map(year => {
                      const yearPayments = memberPayments.filter(
                        p => p.year === year && p.status === 'completed'
                      );
                      const yearTotal = yearPayments.reduce((sum, p) => sum + p.amount, 0);

                      return (
                        <div
                          key={year}
                          className="bg-gradient-to-br from-hdlcc-gray to-hdlcc-darkGray border border-hdlcc-lightGray/10 p-5 rounded-lg"
                        >
                          <h4 className="text-sm text-hdlcc-textTertiary font-semibold">{year}</h4>
                          <p className="text-2xl font-bold mt-2 text-hdlcc-textPrimary">
                            £{yearTotal.toFixed(2)}
                          </p>
                          <p className="text-xs text-hdlcc-textTertiary mt-1.5">
                            {yearPayments.length} payment(s)
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
